import { STORAGE_KEYS } from "@/src/constants/storageKeys";
import type {
  AddPropertyMethod,
  PropertyDetails,
  WizardRoom,
} from "@/src/constants/propertyWizard";
import { propertiesService } from "@/src/services/propertiesService";
import { buildCompletePropertyWizardPayload } from "@/src/utils/buildCompletePropertyWizardPayload";
import { isBrowser } from "@/src/utils/helpers";
import { getRequiredWizardPropertyDetailsErrorKey } from "@/src/utils/wizardPropertyDetailsValidation";

export type SignupPropertyDraft = {
  details: PropertyDetails;
  rooms: WizardRoom[];
  addMethod?: AddPropertyMethod;
};

let pendingSignupPropertyImage: File | null = null;

export function setSignupPropertyImage(file: File | null): void {
  pendingSignupPropertyImage = file;
}

export function takeSignupPropertyImage(): File | null {
  const file = pendingSignupPropertyImage;
  pendingSignupPropertyImage = null;
  return file;
}

const fallbackRoom = (): WizardRoom => ({
  id: "living-room",
  name: "Living Room",
});

export function saveSignupPropertyDraft(draft: SignupPropertyDraft): void {
  if (!isBrowser()) return;
  sessionStorage.setItem(STORAGE_KEYS.SIGNUP_PROPERTY_DRAFT, JSON.stringify(draft));
}

export function readSignupPropertyDraft(): SignupPropertyDraft | null {
  if (!isBrowser()) return null;
  const raw = sessionStorage.getItem(STORAGE_KEYS.SIGNUP_PROPERTY_DRAFT);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SignupPropertyDraft;
    if (!parsed?.details?.addressLine) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasSignupPropertyDraft(): boolean {
  return readSignupPropertyDraft() !== null;
}

export function clearSignupPropertyDraft(): void {
  if (!isBrowser()) return;
  sessionStorage.removeItem(STORAGE_KEYS.SIGNUP_PROPERTY_DRAFT);
}

export async function createSignupPropertyDraftIfPresent(): Promise<boolean> {
  const draft = readSignupPropertyDraft();
  if (!draft) return false;
  const addMethod = draft.addMethod ?? "address";
  const allowManualAddress = addMethod === "manual";
  if (getRequiredWizardPropertyDetailsErrorKey(draft.details, { allowManualAddress })) {
    return false;
  }

  const namedRooms = draft.rooms.filter((room) => room.name.trim());
  const rooms = namedRooms.length > 0 ? namedRooms : [fallbackRoom()];

  try {
    const created = await propertiesService.completeWizard(
      buildCompletePropertyWizardPayload({
        addMethod,
        details: draft.details,
        rooms,
        assets: [],
        childCategories: [],
        setActive: true,
      }),
    );
    clearSignupPropertyDraft();
    const image = takeSignupPropertyImage();
    if (image && created.property?.id) {
      try {
        await propertiesService.uploadImage(created.property.id, image);
      } catch {
        return true;
      }
    }
    return true;
  } catch {
    return false;
  }
}
